import { useState, useEffect, useCallback } from "react";
import { PERSONAL_INFO_KEY, v2_personalInformation } from "../actions/v2/user";
import constant from "../constant";
import { invalidateCachedGet } from "../utils/apiResponseCache";

const USER_DETAILS_KEY = constant.keys.userDetails;
const LEGACY_USER_DETAILS_KEY = "userDetails";

const readCachedUser = () => {
  try {
    let cached = sessionStorage.getItem(USER_DETAILS_KEY);
    if (!cached) {
      cached = sessionStorage.getItem(LEGACY_USER_DETAILS_KEY);
      if (cached) {
        sessionStorage.setItem(USER_DETAILS_KEY, cached);
        sessionStorage.removeItem(LEGACY_USER_DETAILS_KEY);
      }
    }
    return cached ? JSON.parse(cached) : null;
  } catch {
    return null;
  }
};

const writeCachedUser = (user) => {
  sessionStorage.setItem(USER_DETAILS_KEY, JSON.stringify(user));
  sessionStorage.removeItem(LEGACY_USER_DETAILS_KEY);
};

const clearCachedUser = () => {
  sessionStorage.removeItem(USER_DETAILS_KEY);
  sessionStorage.removeItem(LEGACY_USER_DETAILS_KEY);
  invalidateCachedGet(PERSONAL_INFO_KEY);
};

/**
 * Fetches and caches user info in sessionStorage + in-flight GET dedupe.
 */
export const useUserDetails = () => {
  const [user, setUser] = useState(readCachedUser);
  const [loading, setLoading] = useState(!user);
  const [error, setError] = useState(null);

  const fetchUserDetails = useCallback(async () => {
    try {
      setLoading(true);
      const res = await v2_personalInformation();
      if (res.status && res.result) {
        setUser(res.result);
        writeCachedUser(res.result);
        setError(null);
      } else {
        setError(res.message || "Failed to fetch user details");
      }
    } catch (err) {
      console.error("Error fetching user details:", err);
      setError(err.message || "Something went wrong");
    } finally {
      setLoading(false);
    }
  }, []);

  const refreshUser = useCallback(async () => {
    clearCachedUser();
    await fetchUserDetails();
  }, [fetchUserDetails]);

  const clearUser = useCallback(() => {
    clearCachedUser();
    setUser(null);
  }, []);

  useEffect(() => {
    if (!user) fetchUserDetails();
  }, [user, fetchUserDetails]);

  return {
    user,
    loading,
    error,
    refreshUser,
    clearUser,
    setUser,
  };
};
